"use client";
import { useEffect, useId, useMemo, useState } from "react";

import { useTranslations } from "@zapier/i18n";

import { Avatar, Field } from "@zapier/design-system";

import {
  Member,
  Team,
  useAccount,
  useQuerySearchMembersAndTeams,
} from "@zapier/client";
import styles from "./ShareWithPicker.module.css";
import {
  MultiSelectCombobox,
  MultiSelectComboboxItem,
} from "../common/MultiSelectCombobox/MultiSelectCombobox";

const SEARCH_DEBOUNCE_MS = 500;

interface Props {
  isBusy?: boolean;
  isDisabled?: boolean;
  disableEveryoneOption?: boolean;
  showEveryoneOption?: boolean;
  /** Called with the id of the toggled item, e.g. `customuser_1`, `team_2` or `account_3`. */
  onSelect: (id: string) => void;
  /** Ids of items that currently have access. Rendered checked. */
  selectedIds?: string[];
  /** Ids of items that cannot be toggled, e.g. the owner. */
  disabledIds?: string[];
  size?: "compact" | "medium" | "large";
}

interface MemberItem extends MultiSelectComboboxItem {
  name: string;
  imageSrc: string | null;
  disabledReason?: string;
}

const NO_SELECTION: string[] = [];

const isTeam = (memberOrTeam: Member | Team): memberOrTeam is Team =>
  Boolean((memberOrTeam as Team).name);

function ShareWithPicker({
  isBusy = false,
  isDisabled = false,
  disableEveryoneOption = false,
  showEveryoneOption = false,
  onSelect,
  selectedIds = NO_SELECTION,
  disabledIds = NO_SELECTION,
  size = "large",
}: Props) {
  const t = useTranslations("ShareWithPicker");
  const inputId = useId();
  const [inputValue, setInputValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const { account } = useAccount(); // needed to display "Everyone" option
  const { membersAndTeams } = useQuerySearchMembersAndTeams({
    search: searchValue,
  });

  useEffect(() => {
    const timeout = setTimeout(
      () => setSearchValue(inputValue),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timeout);
  }, [inputValue]);

  const items = useMemo(() => {
    const memberItems = membersAndTeams.map((memberOrTeam): MemberItem => {
      if (isTeam(memberOrTeam)) {
        const { id, name, memberCount, logo } = memberOrTeam;
        return {
          id: `team_${id}`,
          name,
          label: t("item-with-detail", {
            name,
            detail: t("member-count", { count: memberCount }),
          }),
          imageSrc: logo,
        };
      }
      const { id, fullName, email, photoUrl } = memberOrTeam;
      const name = fullName || "";
      return {
        id: `customuser_${id}`,
        name,
        label: email ? t("item-with-detail", { name, detail: email }) : name,
        imageSrc: photoUrl,
      };
    });

    const inputMatchesEveryone =
      !inputValue ||
      t("everyone").toLowerCase().includes(inputValue.toLowerCase());

    if (showEveryoneOption && account && inputMatchesEveryone) {
      const everyoneId = `account_${account.id}`;
      const isEveryoneLocked =
        disableEveryoneOption && !selectedIds.includes(everyoneId);
      return [
        {
          id: everyoneId,
          name: t("everyone"),
          label: t("item-with-detail", {
            name: t("everyone"),
            detail: t("member-count", { count: account.memberCount ?? 0 }),
          }),
          imageSrc: account.logoUrl || "",
          disabledReason: isEveryoneLocked ? t("everyone-disabled") : undefined,
        },
        ...memberItems,
      ];
    }

    return memberItems;
  }, [
    membersAndTeams,
    showEveryoneOption,
    inputValue,
    account,
    disableEveryoneOption,
    selectedIds,
    t,
  ]);

  const lockedIds = useMemo(
    () => [
      ...disabledIds,
      ...items.filter((item) => item.disabledReason).map(({ id }) => id),
    ],
    [items, disabledIds],
  );

  const isSearchPending = inputValue !== searchValue;

  return (
    <div className={styles.root}>
      <Field
        inputId={inputId}
        label={t("label")}
        renderInput={({ id }) => (
          <MultiSelectCombobox<MemberItem>
            ariaLabel={t("label")}
            clearSearchLabel={t("clear-search")}
            disabledIds={lockedIds}
            emptyState={
              inputValue && !isSearchPending ? t("no-results") : undefined
            }
            getItemTooltip={(item) => item.disabledReason}
            inputId={id}
            inputValue={inputValue}
            isBusy={isBusy || isSearchPending}
            isDisabled={isDisabled}
            items={items}
            menuAriaLabel={t("menu-label")}
            onInputValueChange={setInputValue}
            onToggleItem={(item) => onSelect(item.id)}
            placeholder={t("placeholder")}
            renderItemIcon={({ item }) => (
              <Avatar name={item.name} size="small" url={item.imageSrc} />
            )}
            selectedIds={selectedIds}
            size={size}
          />
        )}
      />
    </div>
  );
}

export default ShareWithPicker;
